import generateId from '@/utils/generateId'
import type { AxiosResponse } from 'axios'
import Bowser from 'bowser'
import { cloneDeep, isObject } from 'lodash-es'
import { getDeviceId } from './units/device-id'
import { result } from 'lodash'

const browser = Bowser.getParser(window.navigator.userAgent)

export interface LogKeys {
  env: 'p' | 't' | 'd' // 部署环境
  sid: string // 会话id uuid
  cid: string // 客户端id
  ua: string // user agent
  v: string // 版本号
  tp: string // 工具平台（tool platform）页面类型
  // action
  aid: string // 操作id
  e: string // event 事件
  et: string // event type 事件类型
  uid: string | number // 用户id
  uname: string | -1 // 用户名称
  adm_id: number // 管理员id，用来区分是后台登录访问还是前台登录，值从 me 接口的 adm_id 取。
  pid: string | number // 项目id
  scene: string // 场景
  tid: string // api track id
  d: number // duration 持续时间
  extra: any // 额外数据
  status: 3 | 2 | 1 // 状态 1-失败 2-成功 3-无效（表示该事件无效，具体判断跟应用场景相关）
  t: number // timestamp 时间戳
  cc: string | -1 // 国家区域
  sct: string | -1 // 场景类型
  nt: string | -1 // 网络类型
  dl: number | -1 // 下载速度
  dls: number | -1 // 内部资源下载速度
  gdls: number | -1 // 谷歌下载速度
}

export interface LogsOptions
  extends Partial<Pick<LogKeys, 'env' | 'sid' | 'tp' | 'ua' | 'v'>> {
  baseUrl: string
  autoPv?: boolean // 是否自动上报pv
  autoFp?: boolean // 是否自动上报fp
}

export type ActionLogData = Partial<
  Pick<
    LogKeys,
    | 'e'
    | 'et'
    | 'uid'
    | 'uname'
    | 'pid'
    | 'scene'
    | 'tid'
    | 'd'
    | 'extra'
    | 'status'
    | 'aid'
    | 'cc'
    | 'sct'
    | 'nt'
    | 'dl'
    | 'dls'
    | 'gdls'
  >
>

export interface LogsActions {
  log: (data: ActionLogData) => void
  apiLog: (_response: Promise<AxiosResponse> | AxiosResponse) => Promise<any>
}

type TimeRange = [number?, number?]

const defaultLogKeys: Record<keyof LogKeys, -1> = {
  env: -1,
  sid: -1,
  cid: -1,
  ua: -1,
  v: -1,
  tp: -1,
  aid: -1,
  e: -1,
  et: -1,
  uid: -1,
  uname: -1,
  pid: -1,
  scene: -1,
  tid: -1,
  d: -1,
  extra: -1,
  status: -1,
  t: -1,
  cc: -1,
  sct: -1,
  nt: -1,
  dl: -1,
  adm_id: -1,
}

/**
 * 日志上报
 * @param options
 * @returns
 *
 * @example
 * const logs = new Logs({
 *  baseUrl: 'https://playable-stats.mintegral.net/pt',
 *  env: process.env.NODE_ENV === 'production' ? 'p' : 't',
 *  sid: 'TODO uuid',
 *  ua: navigator.userAgent, // TODO 缩略这些信息
 *  v: process.env.TAG,
 *  tp: 've',
 * })
 *
 * // eg: 点击事件
 * const action = logs.createAction()
 * action.log({})
 *
 */
export class Logs {
  _cid: string = getDeviceId()
  _beforeInterceptors: any[] = []
  _tabTimestamp: TimeRange[] = []

  /**
   * 存在用户切换 tab 的行为，当切换 tab 的时候，会导致 duration 异常大，需要减去用户切换出去的时长，需要兼容以下两种场景
   * 1. 关键行为的开始时间在切换时间区间之前
   *   * 关键行为的持续时间等于 duration - 切换时间区间
   * 2. 关键行为的开始时间在切换时间中间
   *   * 关键行为的持续时间等于 date.now() - 切换时间区间的 startTime - 切换时间区间
   */
  getReasonableDuration(duration: number): number {
    const eventStartTimestamp = Date.now() - duration
    const filteredTimeRange = this._tabTimestamp.filter(
      ([switchInTime, switchOutTime]) => {
        // 判断是否在 duration 的范围内
        return (
          switchOutTime !== undefined &&
          switchInTime !== undefined &&
          eventStartTimestamp < switchOutTime
        )
      },
    )

    if (filteredTimeRange.length > 0) {
      // 判断是否事件的开始时间在切换时间区间内，如果存在在时间区间内，则代表开始时间时间戳大于第一个时间区间的开始时间
      const isWithinTimeRange = filteredTimeRange[0]![0]! < eventStartTimestamp
      const totalInactiveTime = filteredTimeRange.reduce(
        (acc, [switchInTime, switchOutTime]) => {
          const inactiveTime = switchOutTime! - switchInTime!
          return acc + inactiveTime
        },
        0,
      )

      // 计算实际有效的 duration
      let adjustedDuration

      if (isWithinTimeRange) {
        adjustedDuration =
          Date.now() - filteredTimeRange[0]![0]! - totalInactiveTime
      } else {
        adjustedDuration = duration - totalInactiveTime
      }

      // 确保调整后的 duration 不会为负值
      if (adjustedDuration > 0) {
        return adjustedDuration
      }
      console.warn(
        'reportData: Switch out range is empty, return original duration',
      )
      return duration
    }
    return duration // 如果没有符合范围的切换记录，则直接返回原始的 duration
  }

  // eslint-disable-next-line no-useless-constructor
  constructor(private options: LogsOptions) {
    const { autoPv = true, autoFp = true } = options
    if (autoPv) {
      // pv
      const pvAction = this.createAction()
      pvAction.log({
        e: 'pv',
      })
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        const timestampTemp: TimeRange = []
        // 客户切换到其他标签页
        timestampTemp.push(Date.now())
        this._tabTimestamp.push(timestampTemp)
      } else {
        // 客户返回当前标签页
        const returnTimestamp = Date.now()
        this._tabTimestamp[this._tabTimestamp.length - 1]?.push(returnTimestamp)
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    if (autoFp) {
      // fp 等待页面加载完成后再执行
      window.addEventListener('load', () => {
        const fpAction = this.createAction()

        const performanceEntry = performance.getEntriesByType('paint')[0]
        const fpTime = performanceEntry?.startTime ?? -1
        console.log('repo fp time: ' + fpTime + 'ms')

        fpAction.log({
          e: 'fp',
          d: fpTime,
        })
      })
    }
  }

  eachBefore(interceptor: <T extends ActionLogData>(data: T) => T): void {
    // 将每次拦截器的回调存起来
    this._beforeInterceptors.push(interceptor)
  }

  _resolveData(data: ActionLogData): ActionLogData {
    // 拦截器
    const result = this._beforeInterceptors.reduce(
      (beforeData, interceptor) => {
        return interceptor(cloneDeep(beforeData))
      },
      data,
    )

    // 时长取整
    if (typeof result.d === 'number') {
      result.d = this.getReasonableDuration(parseInt(result.d, 10))
    }

    return result
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  createAction(options?: Partial<Pick<LogKeys, 'scene'>>): LogsActions {
    const { baseUrl, ...rest } = this.options
    const aid = generateId() // TODO gen uuid
    const connection = window.navigator.connection || null

    const action: LogsActions = {
      // 普通上报
      log: async (data: ActionLogData) => {
        const d = this._resolveData(data)
        reportData(baseUrl, {
          ...cloneDeep(defaultLogKeys),
          ua: `${browser.getBrowserName()}/${browser.getBrowserVersion()}`,
          ...rest,
          t: Date.now(), // timestamp 时间戳
          aid,
          nt: connection ? connection?.effectiveType : -1,
          dl: connection ? connection?.downlink : -1,
          ...(options ?? {}),
          ...d,
          cid: this._cid, // 优先级高，不能被调用者覆盖
        })
      },
      // 接口上报
      apiLog: (
        _response: Promise<AxiosResponse> | AxiosResponse,
      ): Promise<any> => {
        return reportApiLog(action, _response)
      },
    }

    return action
  }

  // 修改属性
  setOptions(data: Partial<LogsOptions>): void {
    Object.assign(this.options, data)
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export async function reportApiLog(
  action: ReturnType<InstanceType<typeof Logs>['createAction']>,
  _response: Promise<AxiosResponse> | AxiosResponse,
) {
  const t = Date.now()
  return Promise.resolve(_response).finally(async () => {
    let traceId = ''
    let status: LogKeys['status'] = 2
    try {
      const response = await _response
      const headers = response.headers
      traceId = headers['x-trace-id']
      status = response.status === 200 && response.data?.code === 0 ? 2 : 1
    } catch (error: any) {
      traceId = (error as AxiosResponse).headers['x-trace-id']
      status = 1
    }

    return action.log({
      e: 'api',
      tid: traceId,
      d: Date.now() - t,
      status,
    })
  })
}

function reportData(url: string, data: any) {
  const omitKeys = ['autoPv', 'autoFp']
  console.log('reportData', JSON.stringify(data))

  const img = new Image()
  let params = ''
  Object.keys(data).forEach((key) => {
    if (omitKeys.includes(key)) return
    const v = isObject(data[key]) ? JSON.stringify(data[key]) : data[key]
    params += `${key}=${encodeURIComponent(v)}&`
  })

  // 去掉最后一个&
  if (params) {
    params = params.slice(0, -1)
  }
  img.src = url + '?' + params
}
